<script>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 * 6. Supports media headers (IMAGE, VIDEO, DOCUMENT) with upload or URL.
 */
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import TemplatePreview from './TemplatePreview.vue';

export default {
  components: {
    TemplatePreview,
  },
  props: {
    template: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'send',
      validator: value => ['send', 'select'].includes(value),
    },
  },
  emits: ['sendMessage', 'resetTemplate'],
  setup(props, { emit }) {
    const processVariable = str => {
      return str.replace(/{{|}}/g, '');
    };

    const allKeysRequired = value => {
      const keys = Object.keys(value);
      return keys.every(key => value[key]);
    };

    const processedParams = ref({});
    const showPreview = ref(false);

    // Media state
    const mediaInputType = ref('upload'); // 'url' | 'upload' | 'id'
    const mediaUrl = ref('');
    const mediaId = ref('');
    const mediaFile = ref(null);
    const mediaPreviewUrl = ref('');
    const isDraggingOver = ref(false);

    const templateString = computed(() => {
      return props.template.components.find(
        component => component.type === 'BODY'
      ).text;
    });

    // Detect header with media
    const headerComponent = computed(() => {
      return props.template.components?.find(
        component => component.type === 'HEADER'
      );
    });

    // Detect footer
    const footerComponent = computed(() => {
      return props.template.components?.find(
        component => component.type === 'FOOTER'
      );
    });

    // Detect buttons
    const buttonsComponent = computed(() => {
      return props.template.components?.find(
        component => component.type === 'BUTTONS'
      );
    });

    const headerFormat = computed(() => {
      return headerComponent.value?.format || null;
    });

    const hasMediaHeader = computed(() => {
      return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value);
    });

    const mediaTypeLabel = computed(() => {
      const labels = {
        IMAGE: 'Imagem',
        VIDEO: 'Vídeo',
        DOCUMENT: 'Documento',
      };
      return labels[headerFormat.value] || '';
    });

    const mediaIcon = computed(() => {
      const icons = {
        IMAGE: 'i-lucide-image',
        VIDEO: 'i-lucide-video',
        DOCUMENT: 'i-lucide-file-text',
      };
      return icons[headerFormat.value] || 'i-lucide-file';
    });

    const acceptedFileTypes = computed(() => {
      const types = {
        IMAGE: 'image/jpeg,image/png,image/webp',
        VIDEO: 'video/mp4,video/3gpp',
        DOCUMENT: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      return types[headerFormat.value] || '*/*';
    });

    const isValidHttpUrl = url => /^https?:\/\//i.test(url || '');

    const currentMediaValue = computed(() => {
      if (mediaInputType.value === 'url') {
        return mediaUrl.value;
      }
      if (mediaInputType.value === 'id') {
        return mediaId.value;
      }
      return mediaPreviewUrl.value;
    });

    const isMediaValid = computed(() => {
      if (!hasMediaHeader.value) return true;
      if (mediaInputType.value === 'upload') {
        return !!mediaFile.value;
      }
      const value = currentMediaValue.value?.trim();
      if (!value) return false;
      if (mediaInputType.value === 'id') return true;
      return isValidHttpUrl(value);
    });

    const variables = computed(() => {
      return templateString.value.match(/{{([^}]+)}}/g);
    });

    const previewParams = computed(() => {
      const params = { body: processedParams.value };

      if (hasMediaHeader.value && currentMediaValue.value) {
        const header = {
          media_type: headerFormat.value?.toLowerCase(),
        };

        const trimmed = currentMediaValue.value.trim();
        if (mediaInputType.value === 'id') {
          header.media_id = trimmed;
        } else {
          header.media_url = trimmed;
        }

        if (mediaFile.value?.name) {
          header.media_name = mediaFile.value.name;
        }

        params.header = header;
      }

      return params;
    });

    const processedString = computed(() => {
      return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = processVariable(variable);
        return processedParams.value[variableKey] || `{{${variable}}}`;
      });
    });

    const v$ = useVuelidate(
      {
        processedParams: {
          requiredIfKeysPresent: requiredIf(variables),
          allKeysRequired,
        },
      },
      { processedParams }
    );

    const generateVariables = () => {
      const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const finalVars = matchedVariables.map(i => processVariable(i));
      processedParams.value = finalVars.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    };

    const revokePreviewUrl = () => {
      if (mediaPreviewUrl.value?.startsWith('blob:')) {
        URL.revokeObjectURL(mediaPreviewUrl.value);
      }
    };

    const processSelectedFile = file => {
      if (!file) return;

      mediaInputType.value = 'upload';

      // Validate file size (max 20MB)
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        useAlert('O arquivo deve ter no máximo 20MB');
        return;
      }

      if (mediaFile.value && mediaFile.value !== file) {
        revokePreviewUrl();
      }

      mediaFile.value = file;

      // Create preview for images and videos
      if (headerFormat.value === 'IMAGE' || headerFormat.value === 'VIDEO') {
        mediaPreviewUrl.value = URL.createObjectURL(file);
      } else {
        mediaPreviewUrl.value = file.name;
      }
    };

    const handleFileSelect = event => {
      const file = event.target.files[0];
      processSelectedFile(file);
    };

    const handleDrop = event => {
      event.preventDefault();
      const file = event.dataTransfer?.files?.[0];
      isDraggingOver.value = false;
      processSelectedFile(file);
    };

    const handleDragOver = event => {
      event.preventDefault();
      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      isDraggingOver.value = true;
    };

    const handleDragLeave = event => {
      event.preventDefault();
      isDraggingOver.value = false;
    };

    const clearMedia = () => {
      revokePreviewUrl();
      mediaFile.value = null;
      mediaUrl.value = '';
      mediaPreviewUrl.value = '';
    };

    const setMediaInputType = type => {
      if (mediaInputType.value === type) return;
      mediaInputType.value = type;
      if (type === 'upload') {
        mediaUrl.value = '';
        mediaId.value = '';
      } else {
        clearMedia();
      }
    };

    const resetTemplate = () => {
      clearMedia();
      emit('resetTemplate');
    };

    const sendMessage = () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      // Validate media if required
      if (hasMediaHeader.value && !isMediaValid.value) {
        if (mediaInputType.value === 'upload') {
          useAlert(`Selecione ou arraste um arquivo de ${mediaTypeLabel.value.toLowerCase()} válido (máx. 20MB)`);
        } else {
          useAlert(`Forneça uma URL válida (http/https) para o header de ${mediaTypeLabel.value.toLowerCase()}`);
        }
        return;
      }

      const bodyParams = processedParams.value || {};
      const enhancedParams = { body: bodyParams };

      if (hasMediaHeader.value) {
        const headerPayload = {
          media_type: headerFormat.value?.toLowerCase(),
        };

        if (mediaInputType.value === 'id' && currentMediaValue.value) {
          headerPayload.media_id = currentMediaValue.value.trim();
        } else if (mediaInputType.value === 'upload' && mediaFile.value) {
          headerPayload.media_source = 'upload';
          headerPayload.media_name = mediaFile.value.name;
          headerPayload.media_size = mediaFile.value.size;
        } else if (currentMediaValue.value) {
          headerPayload.media_url = currentMediaValue.value.trim();
        }

        enhancedParams.header = headerPayload;
      }

      const payload = {
        message: processedString.value,
        templateParams: {
          name: props.template.name,
          category: props.template.category,
          language: props.template.language,
          namespace: props.template.namespace,
          processed_params: enhancedParams,
          footer: footerComponent.value?.text || '',
          buttons: buttonsComponent.value?.buttons || [],
        },
      };

      if (mediaInputType.value === 'upload' && mediaFile.value) {
        payload.mediaFile = mediaFile.value;
      }

      emit('sendMessage', payload);
    };

    const actionButtonLabel = computed(() => {
      return props.mode === 'select'
        ? 'WHATSAPP_TEMPLATES.PARSER.SELECT_TEMPLATE_LABEL'
        : 'WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL';
    });

    onMounted(generateVariables);

    const togglePreview = () => {
      showPreview.value = !showPreview.value;
    };

    return {
      processedParams,
      variables,
      templateString,
      processedString,
      previewParams,
      showPreview,
      togglePreview,
      v$,
      resetTemplate,
      sendMessage,
      actionButtonLabel,
      // Media
      hasMediaHeader,
      headerFormat,
      mediaInputType,
      mediaUrl,
      mediaId,
      mediaFile,
      mediaPreviewUrl,
      currentMediaValue,
      isMediaValid,
      mediaTypeLabel,
      mediaIcon,
      acceptedFileTypes,
      handleFileSelect,
      handleDrop,
      handleDragOver,
      handleDragLeave,
      isDraggingOver,
      clearMedia,
      setMediaInputType,
    };
  },
};
</script>

<template>
  <div
    class="w-full space-y-4"
    @dragover.prevent
    @dragenter.prevent
    @drop.prevent
  >
    <!-- Media Header Card -->
    <div v-if="hasMediaHeader" class="media-card">
      <div class="media-header">
        <i :class="mediaIcon" class="text-n-iris-9" />
        <span class="media-title">Header de {{ mediaTypeLabel }} (obrigatório)</span>
      </div>

      <div class="media-content">
        <!-- Input Type Toggle -->
        <div v-if="!currentMediaValue" class="media-toggle">
          <button
            type="button"
            class="toggle-button"
            :class="{ 'toggle-button--active': mediaInputType === 'upload' }"
            @click="setMediaInputType('upload')"
          >
            <i class="i-lucide-upload" />
            Upload
          </button>
          <button
            type="button"
            class="toggle-button"
            :class="{ 'toggle-button--active': mediaInputType === 'url' }"
            @click="setMediaInputType('url')"
          >
            <i class="i-lucide-link" />
            URL
          </button>
        </div>

        <!-- URL Input -->
        <div v-if="mediaInputType === 'url' && !currentMediaValue" class="media-url-input">
          <input
            v-model="mediaUrl"
            type="url"
            class="url-input"
            placeholder="https://exemplo.com/imagem.jpg"
          />
        </div>

        <!-- Upload Input -->
        <div v-else class="media-upload">
          <template v-if="currentMediaValue">
            <div class="media-preview media-preview--with-action">
              <button
                type="button"
                class="preview-remove"
                @click.prevent="clearMedia"
              >
                <i class="i-lucide-trash-2" />
              </button>
              <img
                v-if="headerFormat === 'IMAGE'"
                :src="currentMediaValue"
                alt="Preview"
                class="preview-image"
              />
              <video
                v-else-if="headerFormat === 'VIDEO'"
                :src="currentMediaValue"
                controls
                class="preview-video"
              />
              <div v-else class="media-preview media-preview--document">
                <i class="i-lucide-file-text text-2xl text-n-iris-9" />
                <span class="text-sm text-n-slate-11 truncate">
                  {{ mediaFile?.name || mediaUrl || 'Documento' }}
                </span>
                <span class="text-[11px] text-n-slate-10">ID/URL anexada</span>
              </div>
            </div>
          </template>
          <template v-else>
            <label
              class="upload-area"
              :class="{
                'upload-area--dragging': isDraggingOver
              }"
              @dragover.stop="handleDragOver"
              @dragenter.stop="handleDragOver"
              @dragleave.stop="handleDragLeave"
              @drop.prevent.stop="handleDrop"
            >
              <input
                type="file"
                class="hidden"
                :accept="acceptedFileTypes"
                @change="handleFileSelect"
              />
              <i class="i-lucide-upload text-n-slate-9 text-xl" />
              <span class="upload-text">Clique para selecionar {{ mediaTypeLabel.toLowerCase() }}</span>
              <span class="upload-hint">Máximo 20MB</span>
            </label>
          </template>
        </div>
      </div>
    </div>

    <!-- Template Preview -->
    <div class="preview-card">
      <div class="preview-header">
        <div class="flex items-center gap-2">
          <i class="i-lucide-file-text text-n-iris-9" />
          <span class="preview-title">Preview do Template</span>
        </div>
        <button type="button" class="preview-toggle" @click="togglePreview">
          <i :class="['i-lucide-chevron-down', showPreview ? 'rotate-180' : '']" />
        </button>
      </div>
      <div v-if="showPreview" class="preview-body">
        <TemplatePreview :template="template" :processed-params="previewParams" />
      </div>
    </div>

    <!-- Variables Card -->
    <div v-if="variables" class="variables-card">
      <div class="variables-header">
        <i class="i-lucide-list text-n-iris-9" />
        <span class="variables-title">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </span>
      </div>

      <div class="variables-list">
        <div
          v-for="(variable, key) in processedParams"
          :key="key"
          class="variable-item"
        >
          <label class="variable-label">
            {{ key }}
          </label>
          <input
            v-model="processedParams[key]"
            type="text"
            class="variable-input"
            :placeholder="`Digite o valor para ${key}`"
          />
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="v$.$dirty && v$.$invalid" class="error-message">
        <i class="i-lucide-alert-circle" />
        <span>{{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}</span>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-footer">
      <button
        type="button"
        class="action-button action-button--back"
        @click="resetTemplate"
      >
        <i class="i-lucide-arrow-left" />
        {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </button>
      <button
        type="button"
        class="action-button action-button--primary"
        @click="sendMessage"
      >
        <i class="i-lucide-send" />
        {{ $t(actionButtonLabel) }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Preview Card */
.preview-card {
  @apply bg-white dark:bg-n-slate-1 border border-n-weak rounded-lg overflow-hidden;
}

.preview-header {
  @apply flex items-center justify-between px-4 py-3 bg-n-alpha-2 dark:bg-n-solid-1 border-b border-n-weak;
}

.preview-title {
  @apply text-sm font-semibold text-n-slate-12;
}

.preview-toggle {
  @apply h-8 w-8 flex items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-3 transition;
}

.preview-toggle i {
  @apply transition-transform duration-200;
}

.preview-body {
  @apply p-4;
}

.template-input {
  @apply w-full p-4 bg-white dark:bg-n-slate-1 text-n-slate-12 text-sm border-0 resize-none focus:outline-none;
  font-family: monospace;
  line-height: 1.6;
}

/* Variables Card */
.variables-card {
  @apply bg-white dark:bg-n-slate-1 border border-n-weak rounded-lg overflow-hidden;
}

.variables-header {
  @apply flex items-center gap-2 px-4 py-3 bg-n-alpha-2 dark:bg-n-solid-1 border-b border-n-weak;

  i {
    @apply text-lg;
  }
}

.variables-title {
  @apply text-sm font-semibold text-n-slate-12;
}

.variables-list {
  @apply p-4 space-y-2;
}

.variable-item {
  @apply flex items-center gap-3 min-h-10;
}

.variable-label {
  @apply inline-flex items-center justify-center h-10 px-2.5 bg-n-slate-3 dark:bg-n-slate-2 text-n-slate-12 text-xs font-medium rounded-md min-w-[44px];
  margin-bottom: 1rem;
}

.variable-input {
  @apply flex-1 h-10 px-3 text-sm bg-n-alpha-2 dark:bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 transition-colors;
  @apply focus:outline-none focus:border-n-iris-6 focus:ring-2 focus:ring-n-iris-2;
  margin-bottom: 0;

  &:hover {
    @apply border-n-slate-6;
  }
}

/* Error Message */
.error-message {
  @apply flex items-center gap-2 mx-4 mb-4 p-3 bg-n-ruby-2 border border-n-ruby-6 rounded-lg text-sm text-n-ruby-11;

  i {
    @apply flex-shrink-0;
  }
}

/* Action Footer */
.action-footer {
  @apply flex items-center justify-end gap-3 pt-2;
}

.action-button {
  @apply inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border;

  i {
    @apply text-base;
  }
}

.action-button--back {
  @apply bg-transparent text-n-slate-12 border-n-weak hover:bg-n-alpha-2;
}

.action-button--primary {
  @apply bg-n-iris-9 text-white border-n-iris-9 hover:bg-n-iris-10 shadow-sm hover:shadow;
}

/* Media Card */
.media-card {
  @apply bg-white dark:bg-n-slate-1 border border-n-weak rounded-lg overflow-hidden;
}

.media-header {
  @apply flex items-center gap-2 px-4 py-3 bg-n-iris-2 dark:bg-n-iris-3 border-b border-n-weak;

  i {
    @apply text-lg;
  }
}

.media-title {
  @apply text-sm font-semibold text-n-slate-12;
}

.media-content {
  @apply p-4 space-y-4;
}

.media-toggle {
  @apply flex gap-2;
}

.toggle-button {
  @apply inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-n-weak bg-transparent text-n-slate-11 transition-all duration-200;

  &:hover {
    @apply bg-n-alpha-2;
  }

  i {
    @apply text-base;
  }
}

.toggle-button--active {
  @apply bg-n-iris-2 dark:bg-n-iris-3 border-n-iris-6 text-n-iris-11;
}

.media-url-input {
  @apply w-full;
}

.url-input {
  @apply w-full px-3 py-2.5 text-sm bg-n-alpha-2 dark:bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 transition-colors;
  @apply focus:outline-none focus:border-n-iris-6 focus:ring-2 focus:ring-n-iris-2;

  &:hover {
    @apply border-n-slate-6;
  }
}

.media-upload {
  @apply w-full;
}

.upload-area {
  @apply flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-n-slate-6 rounded-lg cursor-pointer transition-all duration-200;

  &:hover {
    @apply border-n-iris-6 bg-n-iris-2;
  }
}

.upload-area--dragging {
  @apply border-n-iris-6 bg-n-iris-2/70;
}

.upload-text {
  @apply text-sm text-n-slate-12 text-center;
}

.upload-hint {
  @apply text-xs text-n-slate-10;
}

.clear-button {
  @apply p-1 rounded-full hover:bg-n-alpha-3 text-n-slate-11 transition-colors;
}

.media-preview {
  @apply mt-3 rounded-lg overflow-hidden border border-n-weak relative;
}

.preview-image {
  @apply w-full max-h-48 object-contain bg-n-alpha-2;
}

.preview-video {
  @apply w-full max-h-48;
}

.media-preview--document {
  @apply flex items-center gap-3 p-4 bg-n-alpha-2;
}

.media-preview--with-action {
  @apply relative;
}

.preview-remove {
  @apply absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 dark:bg-n-slate-2 flex items-center justify-center shadow-sm border border-n-weak text-n-slate-11 hover:text-n-ruby-10 transition;
}
</style>
